import React, { isValidElement, useContext, useState } from 'react'

import styled from '@emotion/styled'
import { FormProvider, SubmitErrorHandler, SubmitHandler, useFormContext, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { XOR } from 'ts-essentials'

import {
  ArcadeButton,
  ArcadeDivider,
  ArcadeHelperText,
  ArcadeStatusBanner,
  breakpoints,
  Button,
  ErrorBanner,
  Flex,
  HelperText,
  Hug,
  maxWidths,
  PaddingInset,
  spacing,
  Stack,
  StructuredContent,
  Text,
  ThemeVariant,
  ThemeVariantProvider,
} from '@cash-design-system/react'
// todo: fix this import
// eslint-disable-next-line no-restricted-imports
import { ColorPaletteGray60 } from '@cash-design-system/tokens'
import { disclaimerTextTokenizer } from '@cash-web/dynamic-disclosure'
import { Blockers } from '@cash-web/protos/squareup/franklin/blockers.pb'
import { BlockerDescriptorTheme, BlockersSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { FormContents } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { WebInteractClick } from '@squareup/customer-data-events-web'

import { isContactSupportButton } from '../../util/isContactSupportButton'
import { ButtonActionsContainer } from '../FormBlocker/elements'
import { getSupportedHelpItems } from '../HelpModal/getSupportedHelpItems'
import { HelpModal, HelpModalItem } from '../HelpModal/HelpModal'

export interface BaseBlockerFormFields {
  apiError: string
}

export interface BlockerProps {
  blocker: Blockers[keyof Blockers]
  supplement?: BlockersSupplement[keyof BlockersSupplement]
  skippable?: boolean
}

export interface BaseBlockerProps extends React.HTMLAttributes<HTMLFormElement> {
  mainText?: string
  disableMainTextPadding?: boolean
  secondaryText?: React.ReactNode
  footer?: React.ReactNode
  skippable?: boolean
  shouldStackButtons?: boolean
  buttons?: React.ReactNode[]
  disclaimerText?: React.ReactNode
  helpModal?: { tooltip?: string } & XOR<
    {
      helpItems?: HelpModalItem[]

      title?: string
      subtext?: string
    },
    { helpModalStart: () => void }
  >
  sectionStack?: React.FC
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any, any> // react-hook-form
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: SubmitHandler<any> // onSubmit
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: SubmitErrorHandler<any> // onError
  actionHeader?: React.ReactNode
}

const SectionStack = styled.div`
  display: grid;
  row-gap: ${spacing.l};

  ${breakpoints.tablet} {
    row-gap: ${spacing.xxl};
  }
`

const HeaderStack = styled(Stack)`
  ${breakpoints.tablet} {
    margin-top: 0;
  }
`
const NoMarginStructuredContent = styled(StructuredContent)`
  margin: ${({ theme }) => theme.spacing.empty};
`

export const SubmitButton = ({
  ariaLabel,
  text,
  disabled,
  onClick,
  isLoading,
}: {
  ariaLabel?: string
  disabled?: boolean
  text?: string
  onClick?: () => void
  isLoading?: boolean
}) => {
  const { t } = useTranslation()
  const {
    formState: { isSubmitting, isDirty, isValid },
  } = useFormContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  // Enable the next button if the user has changed anything on the form (isDirty)
  // We also need to check isValid in the case where default values have been set and the user
  // does not need to modify anything
  let formCanBeSubmitted = isDirty || isValid

  // Allow other blockers to control the disabled state
  if (disabled !== undefined) {
    formCanBeSubmitted = !disabled
  }

  // Standardize all "Contact Support" buttons to secondary variant for consistency
  const variant = isContactSupportButton(text) ? 'secondary' : 'primary'
  const arcadeProminence = isContactSupportButton(text) ? 'standard' : 'prominent'

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeButton
        aria-label={ariaLabel}
        disabled={isSubmitting || !formCanBeSubmitted}
        isLoading={isLoading || isSubmitting}
        maxWidth="small"
        onClick={onClick}
        prominence={arcadeProminence}
        size="cta"
        type="submit"
      >
        {text ?? t('Continue', { context: 'Button label.' })}
      </ArcadeButton>
    )
  }

  return (
    <Button
      aria-label={ariaLabel}
      type="submit"
      variant={variant}
      role="button"
      size="large"
      loading={isLoading || isSubmitting}
      disabled={isSubmitting || !formCanBeSubmitted}
      onClick={onClick}
    >
      {text ?? t('Continue', { context: 'Button label.' })}
    </Button>
  )
}

const BreakText = styled.span`
  word-break: break-word;
`

const Footer = styled.div`
  ${({ theme }) => theme.typography.helpText};
  color: ${ColorPaletteGray60};

  /*
   The links we see in the footer may have come from the backend as HTML (like in SelectionBlocker)
   so we need to style them like this if we want the link styles in the Footer to be consistent.
   */
  a {
    color: ${ColorPaletteGray60};
  }
`

const THEME_VARIANTS: Record<string, ThemeVariant> = {
  '#00D54A': 'default', // incorrect Paychecks server color mistake
  '#00D64B': 'default',
  '#00D5FF': 'bitcoin',
  '#5D00E8': 'taxes',
  '#8F12FE': 'investing',
  '#000000': 'monochrome',
}

const getThemeVariant = (theme?: BlockerDescriptorTheme): ThemeVariant | null => {
  const themeColor = theme?.accent_color?.light?.srgb
  if (!themeColor) return null
  const themeVariant = THEME_VARIANTS[themeColor]
  if (!themeVariant) logger.error(new Error(`No theme variant found for color: ${themeColor}`))
  return THEME_VARIANTS[themeColor] ?? null
}

// If no theme is provided, we don't want to overwrite any other existing theme with the default / client-defined theme,
// so omit the ThemeVariantProvider if there is no theme.
const MaybeThemeProvider = ({ children, theme }: { children: React.ReactNode; theme?: BlockerDescriptorTheme }) => {
  const themeVariant = getThemeVariant(theme)
  return themeVariant ? <ThemeVariantProvider themeVariant={themeVariant}>{children}</ThemeVariantProvider> : children
}

// Mitigate empty grid space when the disclaimer is a component that renders nothing
const DisclaimerContainer = styled.div`
  &:empty {
    display: none;
  }
`

export function BaseBlocker({
  mainText,
  disableMainTextPadding,
  secondaryText,
  footer,
  skippable,
  onSubmit,
  onError,
  children,
  form,
  buttons,
  disclaimerText,
  helpModal,
  sectionStack,
  shouldStackButtons,
  actionHeader,
}: BaseBlockerProps): React.ReactElement {
  const { t } = useTranslation()
  const { handleSubmit } = form
  const [showHelp, setShowModal] = useState(false)
  const { clearError, error } = useContext(ErrorContext)
  const flow = useFlowContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const logCdfEvent = useLogCashWebCdfEvent()

  const helpModalFlowStart = helpModal?.helpModalStart
  const supportedHelpItems = getSupportedHelpItems(helpModal?.helpItems) || []
  const hasSupportedHelpItems =
    helpModal && (helpModalFlowStart || (!!supportedHelpItems && supportedHelpItems.length > 0))

  const setShowHelp = (show: boolean, close?: boolean) => {
    if (show && helpModalFlowStart) {
      helpModalFlowStart()
      return
    }
    setShowModal(show)
    logCdfEvent(
      WebInteractClick({
        pageName: flow.active?.trackingFlowName ?? 'BaseBlocker',
        componentName: flow.active?.blockerData?.trackingName ?? 'BaseBlocker',
        componentType: show ? 'Show Help Modal' : 'Close Help Modal',
      })
    )
  }

  const [isSkipping, setSkipping] = useState(false)

  const onSkipBlocker = async () => {
    setSkipping(true)
    await flow.skipBlocker()
  }

  const defaultButtons = [<SubmitButton key="submit" />]
  if (skippable) {
    defaultButtons.unshift(
      <Button size="large" variant="secondary" key="skip" loading={isSkipping} onClick={onSkipBlocker}>
        {t('Skip', { context: 'Button label.' })}
      </Button>
    )
  }

  const actions: React.ReactNode[] = buttons ? buttons : defaultButtons

  const onBaseBlockerSubmit = () => {
    if (isSkipping) {
      setSkipping(false)
      return
    }
    clearError()
    handleSubmit(onSubmit, onError)()
  }

  const tokenizedDisclaimer = typeof disclaimerText === 'string' ? disclaimerTextTokenizer([disclaimerText]) : null
  const disclaimer = tokenizedDisclaimer || disclaimerText

  const hasActions = !!actions && actions.length > 0
  const hasActionsOrFooter = hasActions || !!footer || disclaimer

  const DefaultSectionStack = isArcadeMigrationEnabled ? Stack : SectionStack
  const Section = sectionStack ? sectionStack : DefaultSectionStack

  const theme = flow?.active?.blockerData?.theme

  let mainTextDisplay = null
  if (mainText) {
    if (disableMainTextPadding) {
      mainTextDisplay = (
        <Text typography="pageTitle" as="h2" color="textProminent">
          <BreakText>{mainText}</BreakText>
        </Text>
      )
    } else {
      mainTextDisplay = (
        <PaddingInset>
          <Text typography="pageTitle" as="h2" color="textProminent">
            <BreakText>{mainText}</BreakText>
          </Text>
        </PaddingInset>
      )
    }
  }
  return (
    <FormProvider {...form}>
      <MaybeThemeProvider theme={theme}>
        <Hug maxWidth={maxWidths.content} fullWidth={true}>
          <HelpModal
            isOpen={showHelp}
            onClose={() => setShowHelp(false)}
            helpItems={supportedHelpItems}
            title={helpModal?.title}
            subtext={helpModal?.subtext}
          >
            <Section space={isArcadeMigrationEnabled ? spacing.defaultContent : spacing.empty}>
              {actionHeader && actionHeader}
              {(mainText || secondaryText || helpModal) && (
                <HeaderStack space={spacing.m}>
                  {mainTextDisplay}
                  {secondaryText && (
                    <PaddingInset>
                      <Text typography="body" color="textSubtle">
                        <BreakText>
                          {isValidElement(secondaryText) ? (
                            secondaryText
                          ) : (
                            <NoMarginStructuredContent
                              dangerouslySetInnerHTML={{
                                __html: typeof secondaryText === 'string' ? secondaryText : '',
                              }}
                            />
                          )}
                        </BreakText>
                      </Text>
                    </PaddingInset>
                  )}

                  {hasSupportedHelpItems && (
                    <PaddingInset>
                      {isArcadeMigrationEnabled ? (
                        <ArcadeHelperText
                          onClick={() => setShowHelp(true)}
                          tooltipText={helpModal.tooltip ?? t('Get help', { context: 'Help text.' })}
                        />
                      ) : (
                        <HelperText onClick={() => setShowHelp(true)}>
                          {helpModal.tooltip ?? t('Get help', { context: 'Help text.' })}
                        </HelperText>
                      )}
                    </PaddingInset>
                  )}
                </HeaderStack>
              )}

              <FormContents onSubmit={onBaseBlockerSubmit}>
                <Section space={isArcadeMigrationEnabled ? spacing.defaultContent : spacing.empty}>
                  {children && <Stack space={spacing.m}>{children}</Stack>}
                  {error &&
                    (isArcadeMigrationEnabled ? (
                      <ArcadeStatusBanner variant="error" title={error} />
                    ) : (
                      <ErrorBanner value={error} />
                    ))}
                  {hasActionsOrFooter && (
                    <Stack space={isArcadeMigrationEnabled ? spacing.defaultContent : spacing.l}>
                      {disclaimer && !isArcadeMigrationEnabled ? <PaddingInset>{disclaimer}</PaddingInset> : null}

                      {hasActions && (
                        <ButtonActionsContainer shouldStackButtons={shouldStackButtons}>
                          {actions}
                        </ButtonActionsContainer>
                      )}

                      {disclaimer && isArcadeMigrationEnabled ? (
                        <DisclaimerContainer>{disclaimer}</DisclaimerContainer>
                      ) : null}

                      {footer &&
                        (isArcadeMigrationEnabled ? (
                          <>
                            <ArcadeDivider />
                            <Text typography="body" color="textStandard">
                              <Flex alignItems="center" gap={spacing.xs}>
                                {footer}
                              </Flex>
                            </Text>
                          </>
                        ) : (
                          <PaddingInset>
                            <Footer>{footer}</Footer>
                          </PaddingInset>
                        ))}
                    </Stack>
                  )}
                </Section>
              </FormContents>
            </Section>
          </HelpModal>
        </Hug>
      </MaybeThemeProvider>
    </FormProvider>
  )
}
